import React from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'

import { Heading, PortalPage } from 'component-library-next'
import moment from 'moment/moment'

import { getName } from '@app/common/helpers/getName'
import { Features } from '@app/Feature'
import { isFeatureEnabled } from '@app/Feature/Store/selectors'
import { DeliveryMethod } from '@app/graphql/types'
import { useIntl } from '@app/intl/Intl'
import { Table, TableColumn } from '@app/lib/layout/Table/Table'
import { getMessagesPath } from '@app/modules/Messages'
import { PostponedLetterRequests } from '@app/modules/PostponedLetterRequests/models/LetterRequest'
import { NavBar } from '@app/NavBar'
import { usePostponedLetterRequestsQuery } from '@app/queries/PostponedLetterRequests/PostponedLetterRequests.queries'

export function PostponedLetterRequestsPage() {
  const { formatMessage } = useIntl()
  const { data, error, loading } = usePostponedLetterRequestsQuery({
    fetchPolicy: 'cache-and-network'
  })

  const powerOfAttorneyEnabled = useSelector(isFeatureEnabled(Features.POWER_OF_ATTORNEY))

  if (error) {
    return (
      <Heading size={Heading.sizes.medium}>
        {formatMessage({ id: 'PostponedLetterRequests.Page.GeneralError' })}
      </Heading>
    )
  }

  const columns: Array<TableColumn<PostponedLetterRequests[number]>> = [
    {
      key: 'name',
      label: formatMessage({ id: 'PostponedLetterRequests.Table.Column.Name' }),
      value: (postponedLetterRequest) => (
        <span aria-label='Name'>{getName(postponedLetterRequest.customer)}</span>
      )
    },
    {
      key: 'topic',
      label: formatMessage({ id: 'PostponedLetterRequests.Table.Column.Topic' }),
      value: (postponedLetterRequest) => (
        <Link to={getMessagesPath(postponedLetterRequest.customer.id)}>
          {postponedLetterRequest.question.topic.name}
        </Link>
      )
    },
    {
      key: 'requestFrom',
      label: formatMessage({ id: 'PostponedLetterRequests.Table.Column.RequestFrom' }),
      value: (postponedLetterRequest) =>
        moment(postponedLetterRequest.createdAt).format('DD.MM, HH:mm')
    }
  ]

  if (!!powerOfAttorneyEnabled) {
    columns.push(
      {
        key: 'powerOfAttorney',
        label: formatMessage({ id: 'PostponedLetterRequests.Table.Column.PowerOfAttorney' }),
        value: (postponedLetterRequest) =>
          postponedLetterRequest.powerOfAttorney &&
          formatMessage({
            id: `PowerOfAttorney.DeliveryMethod.${postponedLetterRequest.powerOfAttorney.deliveryMethod}`
          })
      },
      {
        key: 'powerOfAttorneyCreatedAt',
        label: formatMessage({
          id: 'PostponedLetterRequests.Table.Column.PowerOfAttorneyCreatedAt'
        }),
        value: (postponedLetterRequest) =>
          getPowerOfAttorneyCreatedAt(postponedLetterRequest.powerOfAttorney)
      }
    )
  }

  return (
    <NavBar title={formatMessage({ id: 'PostponedLetterRequests.Page.Title' })}>
      <PortalPage hideFooter>
        <Heading size={Heading.sizes.medium}>
          <span>{formatMessage({ id: 'PostponedLetterRequests.Table.Heading' })}</span>
        </Heading>
        <Table
          columns={columns}
          items={data?.postponedRequestsForLawyer || []}
          loading={loading}
          noResults={formatMessage({ id: 'PostponedLetterRequests.Table.NoResults' })}
        />
      </PortalPage>
    </NavBar>
  )
}

function getPowerOfAttorneyCreatedAt(
  powerOfAttorney: PostponedLetterRequests[number]['powerOfAttorney']
): string | undefined {
  if (powerOfAttorney?.deliveryMethod !== DeliveryMethod.Digital) {
    return undefined
  }

  if (!powerOfAttorney.customerDocument) {
    return '---'
  }

  return moment(powerOfAttorney.customerDocument.createdAt).format('DD.MM, HH:mm')
}
